import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn, PROFILE_GONE } from './client.js';

/**
 * What GET /api/me returns. snake_case because the API speaks the database's
 * language and NM-03 makes that snake_case; translating at the boundary would
 * mean two names for every field and a mapping layer to keep in step.
 */
export interface Profile {
  client_id: string;
  adult_account_id: string;
  first_name: string;
  last_name: string;
  country: string;
  /** 'second' for a parent who joined the account (canvas row D): what only the first parent may do is not offered. */
  role: 'holder' | 'second';
  /** The family's country, which the catalogue is sold in: a second parent's own country may differ. */
  account_country: string;
}

export interface Me {
  user_id: string;
  email: string;
  tenant_id: string;
  first_sign_in: boolean;
  profile_complete: boolean;
  /**
   * Whether this person is an approved MPC coach in this tenant.
   *
   * What the Account panel branches on to offer the availability screen. Named
   * for the MPC coach specifically: there are two kinds of coach on one table,
   * and an adult who is a team coach does not sell bookable hours (ruled
   * 24 September), so a flag named for coaches in general would be true for
   * people the screen refuses.
   *
   * It is NOT authorization: every availability route resolves the coach from
   * the token and refuses anybody else, so flipping this in a console gets you
   * a screen that answers 403 to everything it asks for.
   */
  is_mpc_coach: boolean;
  /** The athlete the parent must finish before anything else, or null (31 October). */
  waiver_due_client_id?: string | null;
  /**
   * Absent until profile completion, rather than present and full of nulls.
   *
   * profile_complete is what anything branches on; this is here so the header
   * and the account panel have a name to show without a second request. Reading
   * `me.profile` to decide whether the profile exists would work today and stop
   * working the moment the API returns a partial one for some other reason.
   */
  profile?: Profile;
  /**
   * A family a coach started and this parent claimed (stage C): the name as the
   * coach typed it and the country, for the profile form to start from.
   */
  /** athlete_first: a coach-started family only, null for a second parent. */
  profile_draft?: Pick<Profile, 'first_name' | 'last_name' | 'country'> & { athlete_first: string | null };
  /** A second parent the first parent removed (canvas row D): the removed-access screen, not the profile form. */
  removed_from_family?: true;
}

/**
 * What GET /api/me returns for an athlete (kind: "athlete", 16 October): who
 * they are and nothing else. Kept out of Me deliberately, as its own state
 * below, so no parent or coach screen can read an athlete as an adult with
 * missing fields.
 */
export interface AthleteMe {
  kind: 'athlete';
  /** Whose practices these are (1 November). */
  client_id: string;
  username: string;
  first_name: string;
  last_name: string;
  birth_year: number;
}

export type MeState =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'ready'; me: Me }
  | { status: 'athlete'; athlete: AthleteMe }
  /** An athlete whose parent turned their sign-in off: the API answers sign_in_off. */
  | { status: 'athlete-off' }
  | { status: 'failed'; code: string; message: string };

/**
 * Loads the signed-in customer once they are signed in, and again if they sign
 * in later in the same session.
 */
export function useMe(): { state: MeState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<MeState>({ status: 'signed-out' });
  const [nonce, setNonce] = useState(0);

  // A quiet ask keeps the current screen up while it is answered: the one the
  // API's profile_incomplete triggers (client.ts) should not flash "Checking…".
  const quiet = useRef(false);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const askAgain = () => {
      quiet.current = true;
      setNonce((n) => n + 1);
    };
    window.addEventListener(PROFILE_GONE, askAgain);
    return () => window.removeEventListener(PROFILE_GONE, askAgain);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setState({ status: 'signed-out' });
      return;
    }

    let cancelled = false;
    if (!quiet.current) setState({ status: 'loading' });
    quiet.current = false;

    apiFetch<Me | AthleteMe>('/api/me', getToken)
      .then((me) => {
        if (cancelled) return;
        setState(
          'kind' in me && me.kind === 'athlete' ? { status: 'athlete', athlete: me } : { status: 'ready', me: me as Me },
        );
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) {
          setState({ status: 'signed-out' });
        } else if (cause instanceof ApiError && cause.code === 'sign_in_off') {
          setState({ status: 'athlete-off' });
        } else if (cause instanceof ApiError) {
          setState({ status: 'failed', code: cause.code, message: cause.message });
        } else {
          // Most often the Functions host is not running, which surfaces as a
          // network error rather than a status. Say so, because "failed to
          // fetch" sends people looking at the wrong thing.
          setState({
            status: 'failed',
            code: 'unreachable',
            message:
              'The API did not answer. Is the Functions host running on port 7071?',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, nonce]);

  return { state, reload };
}
